import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import Dashboard from "Pages/Dashboard/Dashboard";
import ReviewGenerator from "Pages/ReviewGenerator/ReviewGenerator";
import AdvanceSEO from "Pages/SEO/AdvanceSEO";
import DigitalProfileContainer from "Pages/DigitalProfile/DigitalProfileContainer";
import ContactSupport from "Pages/ContactSupport/ContactSupport";
import Signup from "Pages/Auth/Signup";
import VerifyEmail from "Pages/Auth/VerifyEmail";
import { ResetPassword } from "Pages/Auth/ResetPassword";
import { ProfilePreview } from "Pages/ProfilePreview/ProfilePreview";
import FeatureAccessWrapper from "@/components/FeatureAccessWrapper";
import MyAccounts from "Pages/MyAccounts/MyAccounts";
import Login from "@/Pages/Auth/Login";
import Onboarding from "@/Pages/Auth/Onboarding";

const RootRedirect = () => {
  const { token } = useSelector((state) => state?.auth);
  const search = window.location.search || "";
  const hash = window.location.hash || "";
  const isGoogleAuthCallback =
    search.includes("code=") ||
    search.includes("error=") ||
    hash.includes("id_token") ||
    hash.includes("access_token") ||
    hash.includes("error=");

  if (isGoogleAuthCallback) {
    return <Login />;
  }

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/login" replace />;
};

let PrivateRoutes = [
  {
    path: "dashboard",
    element: <Dashboard />,
  },
  {
    path: "digital-profile",
    element: <DigitalProfileContainer />,
  },
  {
    path: "review-generator",
    element: (
      <FeatureAccessWrapper featureKey="review-generator">
        <ReviewGenerator />
      </FeatureAccessWrapper>
    ),
  },
  {
    path: "seo",
    element: (
      <FeatureAccessWrapper featureKey="seo">
        <AdvanceSEO />
      </FeatureAccessWrapper>
    ),
  },
  {
    path: "my-accounts",
    element: <MyAccounts />,
  },
];

let nonLoggedInRoutes = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/signup",
    element: <Signup />,
  },
  {
    path: "/confirm-signup",
    element: <VerifyEmail />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
];

export const RenderRoutes = () => {
  const { token } = useSelector((state) => state?.auth);

  return (
    <BrowserRouter>
      <Routes>
        {/* Root route handler */}
        <Route path="/" element={<RootRedirect />} />

        {/* Username onboarding after auth (email/password or Google) */}
        <Route path="/onboarding" element={<Onboarding />} />

        {/* Public routes for non-logged in users */}
        {nonLoggedInRoutes?.map((item, index) => (
          <Route
            path={item?.path}
            key={index}
            element={<PublicRoute element={item?.element} token={token} />}
          />
        ))}

        {/* Private routes for logged in users */}
        {PrivateRoutes?.map((item, index) => (
          <Route
            path={item?.path}
            key={index}
            element={<PrivateRoute element={item?.element} token={token} />}
          />
        ))}

        {/* Public profile preview route */}
        <Route path="profile-preview" element={<ProfilePreview />} />

        {/* Contact Support - accessible to both logged in and non-logged in users */}
        <Route path="contact-support" element={<ContactSupport />} />

        {/* Catch all - if not in route list */}
        <Route
          path="*"
          element={
            token ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
};
